import * as Dialog from '@radix-ui/react-dialog';
import { Eye, EyeOff, Home, KeyRound, Plus, Star } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { ClientPage } from '@/components/client/ClientPage';
import { Button } from '@/components/ui/button';
import { EmptyState, ErrorState } from '@/components/ui/empty-state';
import { Field, FormError, Label } from '@/components/ui/field';
import { RequiredFieldsNote } from '@/components/ui/form-section';
import { StatusBadge } from '@/components/ui/status-badge';
import {
  useClientProperties,
  useClientPropertyAction,
  useSaveClientProperty,
  type ClientPropertyAction,
} from '@/hooks/client-portal';
import { ApiError } from '@/lib/api';
import { useToast } from '@/lib/toast-context';
import { cn } from '@/lib/utils';
import type { ClientPropertyPortalView } from '@shared/schemas/client-portal';

/*
 * /portal/properties — ROADMAP.md §5.3.
 *
 * A client's own addresses, in full: add, edit, choose the primary one, and
 * retire. **No delete** — retiring is the operation, because a clean that
 * happened here still points at the row.
 *
 * Cards, not a table, at every width — this portal's own pattern.
 */

export function PropertiesPage() {
  const properties = useClientProperties();
  const [editing, setEditing] = useState<ClientPropertyPortalView | null>(null);
  const [adding, setAdding] = useState(false);

  const rows = properties.data?.data ?? [];

  return (
    <ClientPage
      title="Properties"
      description="The addresses we clean for you, and how to get into them."
      actions={
        <Button
          onClick={() => {
            setAdding(true);
          }}
        >
          <Plus aria-hidden="true" />
          Add an address
        </Button>
      }
    >
      {properties.error ? (
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          <ErrorState
            title="We could not load your addresses"
            hint="Something went wrong on our side. Try again — and ring the office on (435) 238-3286 if it keeps happening."
            onRetry={() => {
              void properties.refetch();
            }}
          />
        </div>
      ) : properties.isPending ? (
        <div aria-busy="true">
          <span className="sr-only">Loading your addresses</span>
          <div aria-hidden="true" className="h-40 animate-pulse rounded-lg bg-ink-100" />
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          <EmptyState
            icon={Home}
            title="No addresses saved yet."
            hint="Add the address you would like cleaned. You can save more than one — a home and a rental, say — and pick which one is your main address."
            action={
              <Button
                onClick={() => {
                  setAdding(true);
                }}
              >
                <Plus aria-hidden="true" />
                Add an address
              </Button>
            }
          />
        </div>
      ) : (
        <ul className="space-y-3">
          {rows.map((property) => (
            <PropertyCard
              key={property.id}
              property={property}
              onEdit={() => {
                setEditing(property);
              }}
            />
          ))}
        </ul>
      )}

      <PropertyFormDialog open={adding} onOpenChange={setAdding} hasProperties={rows.length > 0} />
      <PropertyFormDialog
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
        hasProperties
        {...(editing ? { property: editing } : {})}
      />
    </ClientPage>
  );
}

function PropertyCard({
  property,
  onEdit,
}: {
  property: ClientPropertyPortalView;
  onEdit: () => void;
}) {
  const toast = useToast();
  const action = useClientPropertyAction();
  const [codesVisible, setCodesVisible] = useState(false);

  function run(next: ClientPropertyAction, message: string) {
    action.mutate(
      { propertyId: property.id, action: next },
      {
        onSuccess: () => {
          toast.success(message);
        },
        onError: (cause) => {
          // The refusal from `retireProperty` lands here in the server's own
          // words — "there is a clean still booked at this address". A generic
          // "that could not be saved" would hide the one thing worth knowing.
          toast.error(cause instanceof ApiError ? cause.message : 'That could not be saved.');
        },
      },
    );
  }

  return (
    <li
      className={cn(
        'rounded-lg border bg-white p-4 shadow-card',
        property.isPrimary ? 'border-honey-500' : 'border-ink-300',
        property.isActive ? '' : 'opacity-70',
      )}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-2 text-h3 text-ink-900">
            {property.nickname ?? property.address}
            {property.isPrimary ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-honey-50 px-2 py-0.5 text-meta font-medium text-bark-900">
                <Star aria-hidden="true" className="size-3 fill-honey-400 text-bark-900" />
                Main address
              </span>
            ) : null}
            {property.isActive ? null : <StatusBadge label="Retired" tone="cancelled" />}
          </p>
          <p className="mt-0.5 text-body text-ink-500">{property.fullAddress}</p>

          <p className="mt-2 text-meta text-ink-700">{describe(property)}</p>

          {property.categoryName ? (
            <p className="mt-1 text-meta text-ink-500">Cleaned as: {property.categoryName}</p>
          ) : null}

          {property.specialInstructions ? (
            <p className="mt-2 text-meta text-ink-700">{property.specialInstructions}</p>
          ) : null}
        </div>

        <div className="flex shrink-0 flex-wrap gap-2">
          {property.isActive && !property.isPrimary ? (
            <Button
              variant="outline"
              size="sm"
              disabled={action.isPending}
              onClick={() => {
                run('primary', `${property.fullAddress} is now your main address.`);
              }}
            >
              Make main
            </Button>
          ) : null}
          <Button variant="outline" size="sm" onClick={onEdit}>
            Edit
          </Button>
          {property.isActive ? (
            <Button
              variant="outline"
              size="sm"
              disabled={action.isPending}
              onClick={() => {
                run('retire', 'Address retired. Ask us any time to bring it back.');
              }}
            >
              Retire
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              disabled={action.isPending}
              onClick={() => {
                run('reactivate', 'Address is back in use.');
              }}
            >
              Bring back
            </Button>
          )}
        </div>
      </div>

      {property.accessNotes ? (
        <div className="mt-3 rounded-sm border border-ink-300 bg-ink-100/40 p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="flex items-center gap-1.5 text-label text-ink-900">
              <KeyRound aria-hidden="true" className="size-4" />
              How we get in
            </p>
            {/*
              Hidden until asked for, exactly as the admin screen does it. Not
              access control — that is server-side — but a lockbox code printed
              on a screen is a lockbox code read by whoever walks past it.
            */}
            <Button
              variant="ghost"
              size="sm"
              aria-expanded={codesVisible}
              onClick={() => {
                setCodesVisible((current) => !current);
              }}
            >
              {codesVisible ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
              {codesVisible ? 'Hide' : 'Show'}
            </Button>
          </div>
          {codesVisible ? (
            <p className="mt-2 whitespace-pre-wrap text-body text-ink-900">
              {property.accessNotes}
            </p>
          ) : (
            <p className="mt-2 text-meta text-ink-500">
              Hidden. Only your cleaner sees this, and only for a clean they are booked on.
            </p>
          )}
        </div>
      ) : null}
    </li>
  );
}

/** "3 bed · 2.5 bath · 1,800 sq ft", leaving out what nobody recorded. */
function describe(property: ClientPropertyPortalView): string {
  const parts: string[] = [];
  if (property.bedrooms !== null) parts.push(`${String(property.bedrooms)} bed`);
  if (property.bathrooms !== null) parts.push(`${String(property.bathrooms)} bath`);
  if (property.squareFeet !== null) parts.push(`${property.squareFeet.toLocaleString()} sq ft`);

  return parts.length > 0 ? parts.join(' · ') : 'Size not recorded';
}

function PropertyFormDialog({
  open,
  onOpenChange,
  property,
  hasProperties,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  property?: ClientPropertyPortalView;
  hasProperties: boolean;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-ink-300 bg-white p-6 shadow-pop">
          <PropertyForm
            property={property}
            hasProperties={hasProperties}
            onDone={() => {
              onOpenChange(false);
            }}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function PropertyForm({
  property,
  hasProperties,
  onDone,
}: {
  property?: ClientPropertyPortalView;
  hasProperties: boolean;
  onDone: () => void;
}) {
  const toast = useToast();
  const isEdit = property !== undefined;
  const save = useSaveClientProperty();

  const [nickname, setNickname] = useState(property?.nickname ?? '');
  const [address, setAddress] = useState(property?.address ?? '');
  const [city, setCity] = useState(property?.city ?? 'Cedar City');
  const [state, setState] = useState(property?.state ?? 'UT');
  const [zip, setZip] = useState(property?.zip ?? '');
  const [bedrooms, setBedrooms] = useState(
    property?.bedrooms == null ? '' : String(property.bedrooms),
  );
  const [bathrooms, setBathrooms] = useState(
    property?.bathrooms == null ? '' : String(property.bathrooms),
  );
  const [squareFeet, setSquareFeet] = useState(
    property?.squareFeet == null ? '' : String(property.squareFeet),
  );
  const [specialInstructions, setSpecialInstructions] = useState(
    property?.specialInstructions ?? '',
  );
  const [accessNotes, setAccessNotes] = useState(property?.accessNotes ?? '');
  const [isPrimary, setIsPrimary] = useState(property?.isPrimary ?? false);

  const error = save.error instanceof ApiError ? save.error : null;
  const fieldErrors = error?.fieldErrors ?? {};

  function submit(event: FormEvent) {
    event.preventDefault();

    save.mutate(
      {
        propertyId: property?.id ?? null,
        input: {
          nickname,
          address,
          city,
          state,
          zip,
          bedrooms: bedrooms === '' ? null : Number(bedrooms),
          bathrooms: bathrooms === '' ? null : Number(bathrooms),
          squareFeet: squareFeet === '' ? null : Number(squareFeet),
          /*
           * Not sent at all. Which checklist a property is cleaned to is the
           * office's decision — it is what a cleaner is judged against — and
           * putting it in a customer's dropdown would let them change the
           * standard of their own clean. The card shows it read-only.
           */
          categoryId: property?.categoryId ?? null,
          specialInstructions,
          accessNotes,
          isPrimary,
        },
      },
      {
        onSuccess: (saved) => {
          toast.success(isEdit ? 'Address saved.' : `Added ${saved.fullAddress}.`);
          onDone();
        },
      },
    );
  }

  return (
    <>
      <Dialog.Title className="text-h2 text-ink-900">
        {isEdit ? 'Edit this address' : 'Add an address'}
      </Dialog.Title>
      <Dialog.Description className="mt-1 text-body text-ink-500">
        This is where your cleaner is sent.
      </Dialog.Description>

      <form onSubmit={submit} className="mt-5 space-y-4">
        <RequiredFieldsNote />

        {error && Object.keys(fieldErrors).length === 0 ? (
          <FormError>{error.message}</FormError>
        ) : null}

        {isEdit ? (
          /*
           * Said plainly rather than enforced. `address_snapshot` is written once
           * when a job is created and never rewritten (business-rules.md §3 rule
           * 5), so a clean already booked keeps the address it was booked with —
           * which is right for the record and surprising if nobody says so.
           */
          <p className="rounded-sm border border-ink-300 bg-honey-50 px-3 py-2.5 text-meta text-bark-900">
            If a clean is already booked here, it keeps the address it was booked with. Ring the
            office on (435) 238-3286 to change where a booked clean happens.
          </p>
        ) : null}

        <Field
          label="Name for this address"
          name="nickname"
          value={nickname}
          onChange={(event) => {
            setNickname(event.target.value);
          }}
          placeholder="Home"
          hint="What you call it. Handy when you have more than one."
        />

        <Field
          label="Street address"
          name="address"
          required
          value={address}
          error={fieldErrors.address}
          onChange={(event) => {
            setAddress(event.target.value);
          }}
        />

        <div className="grid gap-4 sm:grid-cols-3">
          <Field
            label="City"
            name="city"
            required
            value={city}
            error={fieldErrors.city}
            onChange={(event) => {
              setCity(event.target.value);
            }}
          />
          <Field
            label="State"
            name="state"
            required
            value={state}
            error={fieldErrors.state}
            onChange={(event) => {
              setState(event.target.value);
            }}
          />
          <Field
            label="ZIP"
            name="zip"
            required
            value={zip}
            error={fieldErrors.zip}
            onChange={(event) => {
              setZip(event.target.value);
            }}
            placeholder="84720"
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <Field
            label="Bedrooms"
            name="bedrooms"
            type="number"
            min={0}
            value={bedrooms}
            error={fieldErrors.bedrooms}
            onChange={(event) => {
              setBedrooms(event.target.value);
            }}
          />
          <Field
            label="Bathrooms"
            name="bathrooms"
            type="number"
            min={0}
            step={0.5}
            value={bathrooms}
            error={fieldErrors.bathrooms}
            onChange={(event) => {
              setBathrooms(event.target.value);
            }}
            hint="Halves are fine."
          />
          <Field
            label="Square feet"
            name="squareFeet"
            type="number"
            min={0}
            value={squareFeet}
            error={fieldErrors.squareFeet}
            onChange={(event) => {
              setSquareFeet(event.target.value);
            }}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="portal-property-instructions">Anything we should know</Label>
          <textarea
            id="portal-property-instructions"
            name="specialInstructions"
            rows={2}
            value={specialInstructions}
            onChange={(event) => {
              setSpecialInstructions(event.target.value);
            }}
            className="w-full rounded-sm border border-ink-300 bg-white px-3 py-2 text-body"
            placeholder="Park on the street, not the driveway."
          />
        </div>

        <div className="space-y-1.5 rounded-sm border border-ink-300 bg-honey-50 p-3">
          <Label htmlFor="portal-property-access">How we get in</Label>
          <textarea
            id="portal-property-access"
            name="accessNotes"
            rows={3}
            value={accessNotes}
            onChange={(event) => {
              setAccessNotes(event.target.value);
            }}
            className="w-full rounded-sm border border-ink-300 bg-white px-3 py-2 text-body"
            placeholder="Lockbox 4821 on the gas meter. Dog is friendly, keep him inside."
          />
          <p className="text-meta text-bark-900">
            Door or gate codes, where the key is, the dog. Kept hidden on this page until you ask
            for it, and your cleaner only sees it for a clean they are booked on.
          </p>
        </div>

        {hasProperties ? (
          <label className="flex min-h-touch items-center gap-2.5">
            <input
              type="checkbox"
              name="isPrimary"
              checked={isPrimary}
              onChange={(event) => {
                setIsPrimary(event.target.checked);
              }}
              className="size-4 shrink-0 accent-honey-500"
            />
            <span className="text-body text-ink-900">Make this my main address</span>
          </label>
        ) : (
          <p className="text-meta text-ink-500">
            Your first address becomes your main one automatically.
          </p>
        )}

        <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end">
          <Dialog.Close asChild>
            <Button variant="outline" disabled={save.isPending}>
              Cancel
            </Button>
          </Dialog.Close>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : isEdit ? 'Save address' : 'Add address'}
          </Button>
        </div>
      </form>
    </>
  );
}
